import React, { useId } from "react";
import { useController, useFormContext } from "react-hook-form";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import SoftBox from "../../../components/SoftBox";
import SoftInput from "../../../components/SoftInput";
import SoftTypography from "../../../components/SoftTypography";
import { countriesCode } from "../../../constants/countriesCode";

/** Combines an accessible country selector and optional phone number in one input. */
const PhoneInputField = () => {
  const id = useId();
  const { control } = useFormContext();
  const { field: country, fieldState: countryState } = useController({ name: "country", control });
  const {
    field: { ref, ...phone },
    fieldState: phoneState,
  } = useController({ name: "phone", control });
  const selectedCountry = countriesCode.find((option) => option.code === country.value);
  const error = phoneState.error || countryState.error;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <SoftBox mb={1.5}>
      <SoftBox mb={1} ml={0.5} lineHeight={0}>
        <SoftTypography component="label" htmlFor={id} variant="caption" fontWeight="bold">
          Phone{" "}
          <SoftTypography component="span" variant="caption">
            (optional)
          </SoftTypography>
        </SoftTypography>
      </SoftBox>
      <SoftInput
        {...phone}
        id={id}
        inputRef={ref}
        value={phone.value || ""}
        type="tel"
        autoComplete="tel-national"
        placeholder="Phone number"
        error={Boolean(error)}
        inputProps={{ inputMode: "tel", "aria-describedby": errorId }}
        startAdornment={
          <SoftBox
            sx={({ palette }) => ({
              position: "relative",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              gap: 0.5,
              mr: 1.25,
              pr: 1.25,
              borderRight: "1px solid " + palette.grey[300],
            })}
          >
            <SoftTypography
              component="span"
              variant="button"
              fontWeight="medium"
              aria-hidden="true"
              sx={{ whiteSpace: "nowrap", fontSize: 13 }}
            >
              {selectedCountry
                ? `${selectedCountry.code} +${selectedCountry.phone.replace(/-/g, "")}`
                : "Country"}
            </SoftTypography>
            <ExpandMoreIcon aria-hidden="true" sx={{ fontSize: 16 }} />
            <SoftBox
              component="select"
              {...country}
              value={country.value || ""}
              aria-label="Phone country code"
              aria-invalid={Boolean(countryState.error)}
              aria-describedby={errorId}
              title={selectedCountry?.label || "Choose country code"}
              sx={{ position: "absolute", inset: 0, width: "100%", opacity: 0, cursor: "pointer" }}
            >
              <option value="">Country code</option>
              {countriesCode.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label} (+{option.phone})
                </option>
              ))}
            </SoftBox>
          </SoftBox>
        }
        sx={({ palette, boxShadows }) => ({
          display: "flex !important",
          "&:focus-within": {
            borderColor: error ? palette.inputColors.error : palette.inputColors.borderColor.focus,
            boxShadow: error ? boxShadows.inputBoxShadow.error : boxShadows.inputBoxShadow.focus,
          },
        })}
      />
      {error && (
        <SoftBox mt={0.75}>
          <SoftTypography id={errorId} component="div" variant="caption" color="error">
            {error.message}
          </SoftTypography>
        </SoftBox>
      )}
    </SoftBox>
  );
};

export default PhoneInputField;
